import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nosible/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nosible/shared/components/ui/dialog";
import { Edit } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";

type AdminEditMemberRoleProps = {
  memberId: string;
  currentRole: string;
  memberName?: string | null;
  onSuccess?: () => void;
};

const ROLES = ["owner", "admin", "member"] as const;

export function AdminEditMemberRole({
  memberId,
  currentRole,
  memberName,
  onSuccess,
}: AdminEditMemberRoleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(currentRole);

  const utils = trpc.useUtils();
  
  const updateRoleMutation = trpc.admin.updateMemberRole.useMutation({
    onSuccess: () => {
      setIsOpen(false);
      utils.admin.orgBySlug.invalidate();
      onSuccess?.();
    },
  });

  const handleSave = () => {
    if (selectedRole === currentRole) {
      setIsOpen(false);
      return;
    }
    
    updateRoleMutation.mutate({
      memberId,
      role: selectedRole,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Edit className="h-4 w-4 mr-2" />
          Edit Role
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Member Role</DialogTitle>
          <DialogDescription>
            Change the role for {memberName || "this member"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="role-select" className="text-sm font-medium text-chromium-mist">
              Role
            </label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger id="role-select">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    <span className="capitalize">{role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="text-sm text-chromium-mist space-y-1">
            <p className="font-medium">Role Permissions:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><span className="font-medium">Owner:</span> Full access to organization settings and billing</li>
              <li><span className="font-medium">Admin:</span> Can manage members and access API keys</li>
              <li><span className="font-medium">Member:</span> Can view organization and use API keys</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={updateRoleMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateRoleMutation.isPending || selectedRole === currentRole}
          >
            {updateRoleMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
        
        {updateRoleMutation.error && (
          <p className="text-sm text-red-400 mt-2">
            Error: {updateRoleMutation.error.message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
